import { useInventoryStore } from '@/stores/InventoryStore/InventoryStore';
import { handleHttpError } from '@/utils/errorHandlers';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { NeonAlertService } from '@arcual/lib-app-neon';
import {
  type ArcualDigitalDossier,
  type ArcualDocument,
  type ArcualDocumentSignature,
  ArcualDocumentSignatureStatus,
  type ArcualInventoryItem,
  getDocumentsService,
  getInventoryService,
  type HttpError,
} from '@arcual/lib-app-platform';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useAccountStore } from '../AccountStore/AccountStore';
import { type StoreOptions } from '../store.types';

const ArtworksStoreName = 'ArtworksStore';

/**
 * TODO: migrate remaining functions to InventoryStore or introduce a digital dossier store for the digital dossier
 * related functions
 */
export const useArtworksStore = defineStore(ArtworksStoreName, () => {
  const uploadDocuments = async (artwork: ArcualInventoryItem, documents: ArcualDocument[]): Promise<void> => {
    try {
      const promises = documents.map(async (document) => {
        const formData = new FormData();
        formData.append('file', document.key);
        // @ts-expect-error we want to delete the key so it's not submitted
        delete document.key;
        const response = await getDocumentsService().uploadDocument(artwork.id, formData);
        return {
          ...document,
          key: response.id,
        };
      });

      const documentUploadResponse = await Promise.all(promises);
      await getDocumentsService().attachDocuments(artwork.id, documentUploadResponse);

      NeonAlertService.info({ title: backOfficeMessages.success });
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const getDocumentById = async (artworkId: string, documentId: string): Promise<ArcualDocument> => {
    try {
      return await getDocumentsService().getDocumentById(artworkId, documentId);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const loadDigitalDossier = async (
    artwork: ArcualInventoryItem,
    options?: StoreOptions,
  ): Promise<ArcualDigitalDossier> => {
    try {
      const cachedArtwork = await useInventoryStore().getItem(artwork.id);

      if (cachedArtwork.value && options?.invalidateCache) {
        cachedArtwork.value.digitalDossier = undefined;
      }

      let digitalDossier: ArcualDigitalDossier | undefined = cachedArtwork.value?.digitalDossier;

      if (!digitalDossier) {
        digitalDossier = await getDocumentsService().getDocuments(artwork.id);
        if (cachedArtwork.value) {
          cachedArtwork.value.digitalDossier = digitalDossier;
        }
      }

      return digitalDossier;
    } catch (error) {
      if ((error as HttpError).status != 404) {
        handleHttpError(error);
        return Promise.reject(error);
      }
      return null as unknown as ArcualDigitalDossier;
    }
  };

  const deleteDocument = async (artwork: ArcualInventoryItem, document: ArcualDocument): Promise<void> => {
    try {
      await getDocumentsService().deleteDocument(artwork.id, document.id);
      const cachedArtwork = await useInventoryStore().getItem(artwork.id);

      if (cachedArtwork.value?.digitalDossier) {
        cachedArtwork.value.digitalDossier.documents = cachedArtwork.value.digitalDossier.documents.filter(
          (doc) => doc.id !== document.id,
        );
      }

      NeonAlertService.info({ title: backOfficeMessages.success, message: backOfficeMessages.documentDeleted });
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const updateDocument = async (artwork: ArcualInventoryItem, document: ArcualDocument): Promise<ArcualDocument> => {
    try {
      const updatedDocument = await getDocumentsService().updateDocument(artwork.id, document);
      const cachedArtwork = await useInventoryStore().getItem(artwork.id);
      if (cachedArtwork.value?.digitalDossier) {
        cachedArtwork.value.digitalDossier.documents = cachedArtwork.value.digitalDossier.documents.map((doc) =>
          doc.id === updatedDocument.id ? updatedDocument : doc,
        );
      }
      NeonAlertService.info({ title: backOfficeMessages.success, message: backOfficeMessages.documentEdited });
      return updatedDocument;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const downloadDocument = async (artwork: ArcualInventoryItem, document: ArcualDocument): Promise<string> => {
    try {
      return await getDocumentsService().downloadDocument(artwork.id, document.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const createCertificateOfAuthenticity = async (
    artwork: ArcualInventoryItem,
    signature: Omit<ArcualDocumentSignature, 'documentSignatureStatus'>,
  ): Promise<string> => {
    try {
      return await getDocumentsService().createCertificateOfAuthenticity(artwork.id, signature);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const signDocument = async (artwork: ArcualInventoryItem, document: ArcualDocument): Promise<void> => {
    try {
      await getDocumentsService().signDocument(artwork.id, document.id);
      const cachedArtwork = await useInventoryStore().getItem(artwork.id);
      if (cachedArtwork.value?.digitalDossier?.certificateOfAuthenticity?.signatures) {
        cachedArtwork.value.digitalDossier.certificateOfAuthenticity.signatures =
          cachedArtwork.value.digitalDossier.certificateOfAuthenticity.signatures.map((sig) => ({
            ...sig,
            documentSignatureStatus: ArcualDocumentSignatureStatus.SIGNED,
          }));
      }
      NeonAlertService.success({
        title: backOfficeMessages.success,
      });
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const deleteCertificateOfAuthenticity = async (artwork: ArcualInventoryItem): Promise<void> => {
    try {
      await getDocumentsService().deleteCertificateOfAuthenticity(artwork.id);
      const cachedArtwork = await useInventoryStore().getItem(artwork.id);

      if (cachedArtwork.value?.digitalDossier) {
        cachedArtwork.value.digitalDossier.certificateOfAuthenticity = undefined;
      }

      if (cachedArtwork.value?.digitalDossierState) {
        cachedArtwork.value.digitalDossierState.coaSignatureStatus = undefined;
      }

      NeonAlertService.info({ title: backOfficeMessages.success, message: backOfficeMessages.documentDeleted });
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const isRequestedCOASigner = computed(() => (artwork: ArcualInventoryItem) => {
    return !!artwork?.digitalDossier?.certificateOfAuthenticity?.signatures?.some(
      (signature) =>
        signature.signerProfileId === useAccountStore().currentAccount().value?.id &&
        signature.documentSignatureStatus === ArcualDocumentSignatureStatus.PENDING,
    );
  });

  const markAsSold = async (
    artwork: ArcualInventoryItem,
    price: string | number,
    unlistListings: boolean = false,
  ): Promise<boolean> => {
    try {
      const result = await getInventoryService().markItemAsSold(artwork.id, {
        price: String(price),
        unlistListings,
      });

      return result !== null;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const markAsUnsold = async (artwork: ArcualInventoryItem): Promise<boolean> => {
    try {
      const result = await getInventoryService().markItemAsUnsold(artwork.id);

      return result !== null;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  return {
    isRequestedCOASigner,
    uploadDocuments,
    loadDigitalDossier,
    deleteDocument,
    downloadDocument,
    updateDocument,
    createCertificateOfAuthenticity,
    signDocument,
    deleteCertificateOfAuthenticity,
    markAsSold,
    markAsUnsold,
    getDocumentById,
  };
});
